import React, { useState, useEffect, useRef } from 'react';
import AddToCartButton from '/Foundation/React/code/Components/Extranet/AddToCartButtonReact';
import { getRetailer } from '/Foundation/Common/code/Utils/Extranet/Utils';
import { addRecent, clearRecent, getRecent, removeRecent } from './recentSearchesStore.js';

const MIN_QUERY_LENGTH = 3;
const formatText = (text, value) => (text ?? '').replace('{0}', value);

const Search = ({ isMobile }) => {
  const inputReference = useRef(null);
  const fullscreenInputRef = useRef(null);
  const lastInputWasKeyboard = useRef(false);
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [resultsQuery, setResultsQuery] = useState(null);
  const [showExtranetAddToCart, setShowExtranetAddToCart] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);
  const recentButtonRefs = useRef([]);
  const searchRef = useRef(null);
  const inputContainerRef = useRef(null);
  const [accountId] = useState(() => getRetailer()?.ErpId);
  const [recentSearches, setRecentSearches] = useState(() => getRecent(accountId));
  const headerTranslations = window._THULEDATA._MainNavigation.HeaderTranslations;
  const searchTranslation = headerTranslations?.Search;
  const searchLandingPageURL = window._THULEDATA._MainNavigation.SearchLandingPageURL;
  const searchHitsMobile = window._THULEDATA._MainNavigation.SearchHitsMobile;
  const countryCode = window._THULEDATA.SitecoreLanguage;
  const productsText = window._THULEDATA._MainNavigation.SearchProductsText;
  const isExtranet = window._THULEDATA.isExtranet;

  const searchUrl =
    window._THULEDATA._MainNavigation.SearchApiUrl +
    `?query=${searchInput}&numberOfResults=${searchHitsMobile}&language=${countryCode}&imageSize=280`;

  useEffect(() => {
    if (searchInput.length < MIN_QUERY_LENGTH) {
      setSearchProducts([]);
      return;
    }

    let isStale = false;
    const getSearchProducts = () => {
      const options = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      };
      fetch(searchUrl, { ...options, credentials: 'same-origin' })
        .then((response) => response.json())
        .then((data) => {
          if (isStale) return;
          setSearchProducts(data.length > 0 ? data : []);
          setResultsQuery(searchInput);
        })
        .catch(() => {
          if (isStale) return;
          setSearchProducts([]);
          setResultsQuery(searchInput);
        });
    };

    const timer = setTimeout(getSearchProducts, 500);
    return () => {
      isStale = true;
      clearTimeout(timer);
    };
  }, [searchInput]);

  // Text inputs match :focus-visible on click too, so track the modality ourselves
  useEffect(() => {
    const onKeyDown = (e) => (lastInputWasKeyboard.current = e.key === 'Tab' || e.key.startsWith('Arrow'));
    const onPointerDown = () => (lastInputWasKeyboard.current = false);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, []);

  useEffect(() => {
    const retailer = getRetailer();
    if (retailer && retailer.B2BEnable && isExtranet) {
      setShowExtranetAddToCart(true);
    } else {
      setShowExtranetAddToCart(false);
    }
  }, []);

  // Disable body scrolling when search results are visible (desktop) or mobile fullscreen is open
  useEffect(() => {
    const shouldLock = isMobile ? showMobileSearch : isSearchActive;
    const lockClass = 'overflow-hidden--search';
    const html = document.documentElement;
    const body = document.body;

    if (shouldLock) {
      if (!isMobile) {
        html.style.paddingRight = window.innerWidth - html.clientWidth + 'px';
      }
      html.classList.add(lockClass);
      body.classList.add(lockClass);
    } else {
      html.classList.remove(lockClass);
      body.classList.remove(lockClass);
      html.style.paddingRight = '';
    }

    return () => {
      html.classList.remove(lockClass);
      body.classList.remove(lockClass);
      html.style.paddingRight = '';
    };
  }, [isSearchActive, showMobileSearch, isMobile]);

  // The panel is fixed to the viewport and the input isn't centred in it, so recent searches pin to the input's left edge
  useEffect(() => {
    if (isMobile || !isSearchActive) return;
    const alignResults = () =>
      searchRef.current?.style.setProperty(
        '--search-input-left',
        inputContainerRef.current.getBoundingClientRect().left + 'px'
      );
    alignResults();
    window.addEventListener('resize', alignResults);
    return () => window.removeEventListener('resize', alignResults);
  }, [isSearchActive, isMobile]);

  const activeInput = () => (isMobile ? fullscreenInputRef : inputReference);

  const saveRecentSearch = (term) => setRecentSearches(addRecent(accountId, term));

  const resultsUrl = (term) => `${searchLandingPageURL}?search=${encodeURIComponent(term)}`;

  const goToResults = (term) => {
    saveRecentSearch(term);
    return (window.location = resultsUrl(term));
  };

  const hasQuery = searchInput.length >= MIN_QUERY_LENGTH;
  const showRecentSearches = searchInput === '' && recentSearches.length > 0;
  const showProducts = hasQuery && searchProducts.length > 0;
  const showNoResults = isExtranet && hasQuery && resultsQuery === searchInput && searchProducts.length === 0;
  const noResultsHeading = `${headerTranslations?.NoResultsFor ?? ''} “${searchInput}”`;

  const onRecentKeyDown = (e, index) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      recentButtonRefs.current[index + 1]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      (index === 0 ? activeInput().current : recentButtonRefs.current[index - 1])?.focus();
    }
  };

  const showRecentSearchList = () => (
    <div className="search__recent">
      <div className="search__recent-header">
        <p id="recent-searches-header" className="heading6 text-color--black mb-0">
          {headerTranslations?.RecentSearches}
        </p>
        <button
          type="button"
          className="search__recent-clear paragraph--s text-color--gray70"
          aria-label={headerTranslations?.ClearRecentSearches}
          onClick={() => setRecentSearches(clearRecent(accountId))}
        >
          {headerTranslations?.Clear}
        </button>
      </div>
      <ul className="search__recent-list" aria-labelledby="recent-searches-header">
        {recentSearches.map((term, index) => (
          <li key={term} className="search__recent-item">
            <button
              type="button"
              ref={(el) => (recentButtonRefs.current[index] = el)}
              className="search__recent-term"
              onClick={() => goToResults(term)}
              onKeyDown={(e) => onRecentKeyDown(e, index)}
            >
              <i className="le-icon-clock" aria-hidden="true"></i>
              <span className="paragraph text-color--black">{term}</span>
            </button>
            <button
              type="button"
              className="search__recent-remove"
              aria-label={formatText(headerTranslations?.RemoveRecentSearch, term)}
              onClick={(e) => {
                e.stopPropagation();
                setRecentSearches(removeRecent(accountId, term));
              }}
            >
              <i className="le-icon-x" aria-hidden="true"></i>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  const showSearchResults = () => (
    <section
      className={`search__result--product${isExtranet ? ' search__result--rows' : ''}${showProducts ? '' : ' search__result--recent'}`}
    >
      <div className="thule-container">
        <div className="row">
          <div className={`offset-xl-1 col-xl-10 col-xxl-8 offset-xxl-2${isMobile ? ' mt-8' : ''}`}>
            {showProducts ? (
              <>
                {isExtranet ? (
                  <>
                    <div className="search__result-header mb-6">
                      <p className="paragraph--s text-color--gray80 mb-0">{headerTranslations?.SuggestedProducts}</p>
                      <a
                        href={resultsUrl(searchInput)}
                        className="search__view-all heading6 text-color--black"
                        onClick={() => saveRecentSearch(searchInput)}
                      >
                        {headerTranslations?.SeeAllResults}
                        <i className="le-icon-chevron rot-180" aria-hidden="true"></i>
                      </a>
                    </div>
                    <div className="search-result-rows">{showSearchRows()}</div>
                  </>
                ) : (
                  <>
                    <p className="paragraph--s text-color--gray80 mb-6">{productsText}</p>
                    <div className="search-products-grid">{showSearchProduct()}</div>
                  </>
                )}
              </>
            ) : showNoResults ? (
              <div className="search__no-results">
                <p className="heading5 text-color--black mb-0">{noResultsHeading}</p>
              </div>
            ) : (
              showRecentSearches && showRecentSearchList()
            )}
          </div>
        </div>
      </div>
    </section>
  );

  const showSearchRows = () =>
    searchProducts.map((item, i) => (
      <article key={i} className="search-result-row">
        <a
          href={item.Link}
          className="search-result-row__thumb"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => saveRecentSearch(searchInput)}
        >
          <img src={item.ProductImage} alt="" />
        </a>
        <a
          href={item.Link}
          className="search-result-row__name heading5 text-color--black text--truncate"
          onClick={() => saveRecentSearch(searchInput)}
        >
          {item.Name}
        </a>
        <p className="search-result-row__description mb-0 paragraph--s text-color--black" title={item.Webtitle2}>
          {item.Webtitle2}
        </p>
        <p className="search-result-row__sku mb-0 paragraph--s text-color--gray60 text--truncate">{item.ErpOutdoor}</p>
        {showExtranetAddToCart && (
          <AddToCartButton
            variant="row"
            productData={item}
            productId={item.ErpOutdoor}
            translations={headerTranslations}
            onAddToCart={() => saveRecentSearch(searchInput)}
          />
        )}
      </article>
    ));

  const showSearchProduct = () =>
    searchProducts.map((item, i) => (
      <article key={i}>
        <a href={item.Link}>
          <div className="search__product d-flex ">
            <div className="product__imageContainer">
              <img src={item.ProductImage} alt={item.ImageAlt} />
            </div>
            <div className="product__text mt-3 d-flex align-items-center">
              <div>
                <p className="mb-2 heading5 text-color--gray80">{item.Name}</p>
                {item.Webtitle2 && <p className="paragraph--s text-color--gray60">{item.Webtitle2}</p>}
              </div>
            </div>
          </div>
        </a>
      </article>
    ));

  const clearSearch = () => {
    setSearchInput('');
    setSearchProducts([]);
    if (isMobile) {
      setShowMobileSearch(false);
    } else {
      setIsSearchActive(false);
      inputReference.current.blur();
    }
  };

  // Focus the fullscreen input when mobile search opens
  useEffect(() => {
    if (showMobileSearch && fullscreenInputRef.current) {
      fullscreenInputRef.current.focus();
    }
  }, [showMobileSearch]);

  const onSearchBtnClick = (e) => {
    e.preventDefault();
    if (searchInput.length === 0) return;
    goToResults(searchInput);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      clearSearch();
    } else if (e.key === 'ArrowDown' && e.target === activeInput().current && searchInput === '') {
      if (recentButtonRefs.current[0]) {
        e.preventDefault();
        recentButtonRefs.current[0].focus();
      }
    }
  };

  const resultsAnnouncement = (
    <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {showProducts
        ? `${searchProducts.length} ${headerTranslations?.Results ?? ''}`
        : showNoResults
          ? noResultsHeading
          : ''}
    </span>
  );

  // Mobile: show trigger bar + fullscreen overlay
  if (isMobile) {
    return (
      <>
        {/* Always-visible trigger search bar */}
        <div className="search search--mobile-trigger">
          <div className="searchInputContainer__wrapper">
            <div className="searchInputContainer bg-color--gray10" onClick={() => setShowMobileSearch(true)}>
              <i className="le-icon-search"></i>
              <div className="p-3 w-100 mainNavigataion__searchInput paragraph text-color--gray60">
                {searchTranslation}
              </div>
            </div>
          </div>
        </div>

        {/* Fullscreen overlay */}
        {showMobileSearch && (
          <div className="search search--mobile-fullscreen" onKeyDown={onSearchKeyDown}>
            <div className="searchInputContainer__wrapper mt-5">
              <div
                className={`searchInputContainer bg-color--gray10${hasKeyboardFocus ? ' searchInputContainer--keyboard-focus' : ''}`}
              >
                <i className="le-icon-search"></i>
                <form autoComplete="off" onSubmit={onSearchBtnClick}>
                  <label htmlFor="mainNavigataion__searchInput" className="sr-only">
                    Search input
                  </label>
                  <input
                    ref={fullscreenInputRef}
                    className="w-100 mainNavigataion__searchInput paragraph text-color--black"
                    type="text"
                    placeholder={searchTranslation}
                    name="mainNavigataion__searchInput"
                    id="mainNavigataion__searchInput"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onFocus={() => setHasKeyboardFocus(lastInputWasKeyboard.current)}
                    onBlur={() => setHasKeyboardFocus(false)}
                  ></input>
                </form>
              </div>
              <div className="ms-7 search__closeBtn">
                <button onClick={() => clearSearch()} className="btn btn-close p-0">
                  <span className="sr-only">Close search</span>
                </button>
              </div>
            </div>
            <div className="search__border"></div>
            {(showProducts || showNoResults || showRecentSearches) && showSearchResults()}
            {resultsAnnouncement}
          </div>
        )}
      </>
    );
  }

  // Desktop
  return (
    <>
      <div ref={searchRef} className={`search${isSearchActive ? ' search--active' : ''}`} onKeyDown={onSearchKeyDown}>
        <div className="searchInputContainer__wrapper">
          <div
            ref={inputContainerRef}
            className={`searchInputContainer bg-color--gray10${hasKeyboardFocus ? ' searchInputContainer--keyboard-focus' : ''}`}
          >
            <i className="le-icon-search"></i>
            <form autoComplete="off" onSubmit={onSearchBtnClick}>
              <label htmlFor="mainNavigataion__searchInput" className="sr-only">
                Search input
              </label>
              <input
                ref={inputReference}
                className="w-100 mainNavigataion__searchInput paragraph text-color--black"
                type="text"
                placeholder={searchTranslation}
                name="mainNavigataion__searchInput"
                id="mainNavigataion__searchInput"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onFocus={() => {
                  setIsSearchActive(true);
                  setHasKeyboardFocus(lastInputWasKeyboard.current);
                }}
                onBlur={() => setHasKeyboardFocus(false)}
              ></input>
            </form>
          </div>
        </div>
        {isSearchActive && (
          <div className="search__closeBtn">
            <button
              onClick={() => {
                clearSearch();
              }}
              className="btn btn-close p-0"
            >
              <span className="sr-only">Close search</span>
            </button>
          </div>
        )}
        <div className="search__border"></div>
        {isSearchActive && (showProducts || showNoResults || showRecentSearches) && showSearchResults()}
        {resultsAnnouncement}
        <div
          className={`searchOverlay d-none d-lg-block${isSearchActive ? ' searchOverlay--visible' : ''}`}
          aria-hidden="true"
          onClick={() => clearSearch()}
        ></div>
      </div>
    </>
  );
};
export default Search;
